"use client";

import { useTranslations } from "next-intl";
import { Button } from "@ui/components/core/Button";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { Reframe } from "@ui/components/composers/Reframe";
import { type UseContactSalesFormV2Return } from "../hooks/useContactSalesFormV2";
import { type ContactSalesFormValues } from "../shared/types";
import { getAISDRUrl, isPersonalEmail } from "../shared/utils";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

export function FinalScreen({
  reset,
  formData,
  submissionId,
}: Pick<UseContactSalesFormV2Return, "reset"> & {
  formData: ContactSalesFormValues;
  submissionId: string;
}) {
  const t = useTranslations("ClientKeys.contact_sales_page.form");

  const aiSDRUrl = getAISDRUrl(formData.country, formData.company, formData.first_name, formData.email, submissionId);

  const formEventProperties = {
    cs_v2_submission_id: submissionId,
    cs_v2_country: formData.country,
    cs_v2_is_personal_email: isPersonalEmail(formData.email),
    cs_v2_company_size: formData.company_size,
    cs_v2_industry: formData.industry,
    cs_v2_how_did_you_hear: formData.how_did_you_hear,
    cs_v2_company: formData.company,
  };

  const handleStartNewSubmission = () => {
    captureEvent(EventName.mw_contact_sales_v2_start_new_submission_clicked, {
      cs_v2_previous_submission_id: submissionId,
    });
    reset();
  };

  return (
    <div className='tw-flex tw-flex-col tw-items-start tw-text-left'>
      {aiSDRUrl && (
        <>
          <p className='tw-text-sm tw-mb-6 tw-max-w-2xl tw-text-black'>{t("ai_sdr_description")}</p>
          <div className='tw-mb-8 tw-w-full tw-max-w-3xl'>
            <LinkTracked
              href={aiSDRUrl}
              target='_blank'
              rel='noopener noreferrer'
              asButton
              buttonVariants={{ variant: "primary", size: "lg", expand: true }}
              eventName={EventName.mw_contact_sales_v2_ai_sdr_clicked}
              eventProperties={formEventProperties}
            >
              {t("chat_with_ai_sdr")}
            </LinkTracked>
          </div>
        </>
      )}
      {!aiSDRUrl && (
        <div className='tw-text-sm tw-mb-8 tw-max-w-3xl tw-space-y-4 tw-text-black'>
          <p>
            <LinkTracked
              href='/blog?category=customer-stories'
              className='tw-text-blue-500 tw-underline hover:tw-no-underline'
              variant='clean'
              eventName={EventName.mw_contact_sales_v2_final_screen_link_clicked}
              eventProperties={{ ...formEventProperties, cs_v2_link_type: "customer_stories" }}
            >
              {t("get_inspired")}
            </LinkTracked>{" "}
            <span>{t("get_inspired_description")}</span>{" "}
            <LinkTracked
              href='/blog/voiply'
              className='tw-text-blue-500 tw-underline hover:tw-no-underline'
              variant='clean'
              eventName={EventName.mw_contact_sales_v2_final_screen_link_clicked}
              eventProperties={{ ...formEventProperties, cs_v2_link_type: "blog_voiply" }}
            >
              {t("sales")}
            </LinkTracked>
            ,{" "}
            <LinkTracked
              href='/blog/rauda-ai'
              className='tw-text-blue-500 tw-underline hover:tw-no-underline'
              variant='clean'
              eventName={EventName.mw_contact_sales_v2_final_screen_link_clicked}
              eventProperties={{ ...formEventProperties, cs_v2_link_type: "blog_rauda" }}
            >
              {t("customer_support")}
            </LinkTracked>
            , {t("and")}{" "}
            <LinkTracked
              href='/blog/traba'
              className='tw-text-blue-500 tw-underline hover:tw-no-underline'
              variant='clean'
              eventName={EventName.mw_contact_sales_v2_final_screen_link_clicked}
              eventProperties={{ ...formEventProperties, cs_v2_link_type: "blog_traba" }}
            >
              {t("staffing")}
            </LinkTracked>
            .
          </p>
          <p>
            {t("head_to_webinar")}{" "}
            <LinkTracked
              href='/webinars'
              className='tw-text-blue-500 tw-underline hover:tw-no-underline'
              variant='clean'
              eventName={EventName.mw_contact_sales_v2_final_screen_link_clicked}
              eventProperties={{ ...formEventProperties, cs_v2_link_type: "webinar" }}
            >
              {t("webinar_page")}
            </LinkTracked>{" "}
            {t("webinar_description")}
          </p>
          <p>
            {t("or")}{" "}
            <LinkTracked
              href='https://elevenlabs.io/app/sign-up'
              className='tw-text-blue-500 tw-underline hover:tw-no-underline'
              variant='clean'
              eventName={EventName.mw_contact_sales_v2_final_screen_link_clicked}
              eventProperties={{ ...formEventProperties, cs_v2_link_type: "sign_up_free" }}
            >
              {t("sign_up_free")}
            </LinkTracked>{" "}
            {t("try_platform_yourself")}
          </p>
        </div>
      )}
      <div className='tw-w-full tw-max-w-3xl tw-mb-8'>
        <Reframe embed='<iframe width="560" height="315" src="https://www.youtube.com/embed/v-EYzZCLF48" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>' />
      </div>
      <div className='tw-w-full tw-max-w-3xl'>
        <p className='tw-text-sm tw-mb-6 tw-text-black'>{t("to_submit_updated_details")}</p>
        <Button type='button' onClick={handleStartNewSubmission} variant='secondary' size='lg' expand>
          {t("start_new_submission")}
        </Button>
      </div>
    </div>
  );
}
